import { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@/components/ui/modal'
import { useAuth } from '@/hooks/use-auth'
import { useCreateInvoiceAdjustment, useInvoiceAdjustments } from '@/hooks/use-billing'
import { getApiErrorMessage } from '@/lib/api'
import { formatKES } from '@/lib/utils'

export function CreateAdjustmentModal({
  invoiceId,
  invoiceNumber,
  invoiceAmount,
  memberId,
  onClose,
  onCreated,
}: {
  invoiceId: string
  invoiceNumber: string
  invoiceAmount: number
  memberId: string
  onClose: () => void
  onCreated: () => void
}) {
  const { user } = useAuth()
  const isEstateManager = user?.role === 'estate_manager'
  const createAdjustment = useCreateInvoiceAdjustment()

  const {
    adjustmentsTotal,
    adjustedTotal: currentTotal,
    isLoading: adjustmentsLoading,
  } = useInvoiceAdjustments({ id: invoiceId, memberId, amount: invoiceAmount })

  const [correctTotal, setCorrectTotal] = useState('')
  const [notes, setNotes] = useState('')
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!adjustmentsLoading) setCorrectTotal(String(currentTotal))
  }, [adjustmentsLoading, currentTotal])

  const correctNum = parseFloat(correctTotal)
  const difference = Number.isFinite(correctNum)
    ? Math.round((correctNum - currentTotal) * 100) / 100
    : null

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)

    if (difference === null || correctNum < 0) {
      setError('Enter a valid amount')
      return
    }
    if (difference === 0) {
      setError('The correct amount matches the current total — nothing to adjust')
      return
    }
    if (isEstateManager && !reason.trim()) {
      setError('A reason is required to request approval')
      return
    }

    try {
      const result = await createAdjustment.mutateAsync({
        invoiceId,
        amount: difference,
        ...(notes && { notes }),
        ...(reason.trim() && { reason: reason.trim() }),
      })
      if (result.data.pendingApproval) {
        alert('Adjustment requested — sent for committee/admin approval.')
      }
      onCreated()
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to create adjustment'))
    }
  }

  return (
    <Modal open onClose={onClose} labelledBy="create-adjustment-title" panelClassName="sm:max-w-sm">
      <ModalHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-sans text-label uppercase tracking-[0.12em] text-muted-foreground">
              {invoiceNumber}
            </p>
            <h2 id="create-adjustment-title" className="font-serif text-xl text-ink">
              Add adjustment
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-ink"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>
      </ModalHeader>
      <form onSubmit={(e) => void handleSubmit(e)} className="flex min-h-0 flex-1 flex-col">
        <ModalBody className="space-y-4">
          <p className="font-sans text-sm text-muted-foreground">
            Corrects this already-paid invoice. Current total:{' '}
            <span className="font-medium text-ink">
              {adjustmentsLoading ? '…' : formatKES(currentTotal)}
            </span>
            {!adjustmentsLoading && adjustmentsTotal !== 0 && (
              <>
                {' '}
                ({formatKES(invoiceAmount)} original {adjustmentsTotal > 0 ? '+' : '−'}{' '}
                {formatKES(Math.abs(adjustmentsTotal))} adjustments)
              </>
            )}
          </p>
          <Input
            label="Correct amount for this invoice (KES)"
            type="number"
            step="any"
            min={0}
            value={correctTotal}
            onChange={(e) => setCorrectTotal(e.target.value)}
            disabled={adjustmentsLoading}
            required
          />
          {difference !== null && difference !== 0 && (
            <p className="rounded border border-border-2 bg-cream-2 px-3 py-2 font-sans text-sm text-ink">
              {difference > 0
                ? `This creates a supplementary charge of ${formatKES(difference)}.`
                : `This creates a credit note of ${formatKES(-difference)}.`}
            </p>
          )}
          <div className="space-y-1.5">
            <label htmlFor="adjustment-notes" className="font-sans text-sm font-medium text-ink">
              Notes (optional)
            </label>
            <textarea
              id="adjustment-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="flex w-full rounded border border-input bg-card px-3.5 py-2 font-sans text-sm text-ink focus-visible:border-primary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-green/20"
            />
          </div>
          {isEstateManager && (
            <>
              <p className="rounded border border-status-pending-bg bg-status-pending-bg px-3 py-2 font-sans text-xs text-status-pending-text">
                This will be sent to committee/admin for approval before it takes effect.
              </p>
              <Input
                label="Reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. resident overpaid, confirmed with them"
                required
              />
            </>
          )}
          {error && (
            <p className="font-sans text-xs text-destructive" role="alert">
              {error}
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            loading={createAdjustment.isPending}
            disabled={adjustmentsLoading || !difference}
          >
            {isEstateManager ? 'Request adjustment' : 'Create adjustment'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  )
}
